import { Component, computed, EventEmitter, inject, input, output, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import {
  Attachment,
  AttachmentUploadJob,
  FormControlContext,
} from '@shared/form-controls/attachment-upload/attachment-utils';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AttachmentDetailDto, AttachmentFolderItemDto, UserItemDto, UserService, } from '@artemis-software/wr-api';
import { localStorageSignal } from '@/utils/signals/localStorageSignal';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { AttachmentDialogComponent } from '@components/dialog/attachment-dialog/attachment-dialog.component';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { toDate } from '@/utils/date-utils';

const fileTypeIconRecord: Record<AttachmentDetailDto.TypeEnum, string> = {
  FILE: 'file-text-outline',
  IMAGE: 'image-outline',
  VIDEO: 'video-outline',
  AUDIO: 'music-outline',
  DOCUMENT: 'file-text-outline',
};

export interface AttachmentFolderInstance {
  id?: string;
  attachmentFolder?: AttachmentFolderItemDto;
  name: string;
  color: string;
  targetId: string;
  parentId?: string;
  attachments?: Attachment[];
  subFolders?: AttachmentFolderInstance[];
  publicFolder?: boolean;
}

export interface BuildingDocumentsValue {
  folderInstances: AttachmentFolderInstance[];
  rootAttachments?: Attachment[];
}

const ROOT_CONTEXT_KEY = '__root__';

export type AttachmentWithProfilePicture = Attachment & {
  fullName: string;
  profilePicture?: string;
};

export interface AttachmentYearGroup {
  year: number | null;
  attachments: AttachmentWithProfilePicture[];
}

interface DatedAttachment {
  attachment: AttachmentWithProfilePicture;
  timestamp: number | null;
}

function attachmentTimestamp(created: string | undefined): number | null {
  if (!created) {
    return null;
  }
  const timestamp = toDate(created).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

@Component({
  standalone: false,
  selector: 'wr-attachment-folder-tree',
  templateUrl: './attachment-folder-tree.component.html',
  styleUrls: ['./attachment-folder-tree.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AttachmentFolderTreeComponent,
      multi: true,
    },
  ],
})
export class AttachmentFolderTreeComponent extends AbstractFormControl<BuildingDocumentsValue> {
  readonly nbDialogService = inject(NbDialogService);
  readonly nbToastrService = inject(NbToastrService);
  readonly userService = inject(UserService);

  readonly fileTypeIconRecord = fileTypeIconRecord;

  readonly allowSubFolderManagement = input(false);
  readonly allowAttachmentEditing = input(true);

  readonly createSubFolder = output<AttachmentFolderInstance>();
  readonly renameSubFolder = output<AttachmentFolderInstance>();
  readonly deleteSubFolder = output<AttachmentFolderInstance>();

  readonly folders = computed(() => this.valueSignal()?.folderInstances ?? []);

  readonly rootAttachments = computed(() => this.valueSignal()?.rootAttachments);

  readonly showRootDocuments = computed(() => this.rootAttachments() !== undefined);

  readonly selectedFolderId = localStorageSignal<string | null>('selectedFolderId', null);

  readonly selectedFolder = computed(() => {
    const selectedId = this.selectedFolderId();
    return selectedId ? this.findFolderById(selectedId) : undefined;
  });

  readonly selectedRootFolder = computed(() => {
    const folder = this.selectedFolder();
    if (!folder) {
      return undefined;
    }
    if (!folder.parentId) {
      return folder;
    }
    return this.folders().find((root) => root.id === folder.parentId);
  });

  readonly isSubFolderSelected = computed(() => !!this.selectedFolder()?.parentId);

  readonly reloadAttachmentsWithProfilePictures = signal(0);

  readonly attachmentsWithProfilePictures = asyncComputed(async () => {
    this.reloadAttachmentsWithProfilePictures();
    const attachments = (this.selectedFolder()?.attachments ?? this.rootAttachments() ?? [])
      .filter((attachment) => !attachment.deleted);
    const userIds = new Set(attachments.map(a => a.creatorId!).filter(id => !!id));
    let users: UserItemDto[];
    if (userIds.size > 0) {
      users = await firstValueFrom(this.userService.findByIds([...userIds]));
    }
    return attachments.map((attachment): AttachmentWithProfilePicture => {
      const user = users?.find(u => u.id === attachment.creatorId);
      return {
        ...attachment,
        fullName: user ? `${user.firstName} ${user.lastName}` : '',
        profilePicture: user ? user.userProfilePicture?.presignedUrl : 'https://i.imgur.com/ujW6tKI.png',
      };
    });
  });

  readonly attachmentYearGroups = computed<AttachmentYearGroup[]>(() => {
    const attachments = this.attachmentsWithProfilePictures() ?? [];
    const groups = new Map<number | null, DatedAttachment[]>();
    for (const attachment of attachments) {
      const timestamp = attachmentTimestamp(attachment.created);
      const year = timestamp === null ? null : new Date(timestamp).getFullYear();
      const group = groups.get(year);
      if (group) {
        group.push({ attachment, timestamp });
      } else {
        groups.set(year, [{ attachment, timestamp }]);
      }
    }
    return [...groups.entries()]
      .map(([year, dated]) => ({
        year,
        attachments: dated
          .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0))
          .map((entry) => entry.attachment),
      }))
      .sort((a, b) => {
        if (a.year === null) {
          return 1;
        }
        if (b.year === null) {
          return -1;
        }
        return b.year - a.year;
      });
  });

  readonly folderContext = new Map<string, FormControlContext>();

  selectFolder(folder: AttachmentFolderInstance) {
    this.selectedFolderId.set(folder.id!);
  }

  goBack() {
    const folder = this.selectedFolder();
    this.selectedFolderId.set(folder?.parentId ?? null);
  }

  getFullFileName(attachment: Attachment): string {
    return `${attachment.fileName}.${attachment.fileEnding}`;
  }

  getContextForInstance(folder: AttachmentFolderInstance): FormControlContext {
    const folderId = folder.id!;
    if (!this.folderContext.has(folderId)) {
      const eventEmitter = new EventEmitter<Attachment[]>();
      eventEmitter.subscribe((attachments) =>
        this.attachmentsAddedToFolder(folderId, attachments),
      );
      this.folderContext.set(folderId, {
        newAttachmentsEventListener: eventEmitter,
        pendingJobs$: new BehaviorSubject<AttachmentUploadJob[]>([]),
      });
    }
    return this.folderContext.get(folderId)!;
  }

  getRootContext(): FormControlContext {
    if (!this.folderContext.has(ROOT_CONTEXT_KEY)) {
      const eventEmitter = new EventEmitter<Attachment[]>();
      eventEmitter.subscribe((attachments) => this.attachmentsAddedToRoot(attachments));
      this.folderContext.set(ROOT_CONTEXT_KEY, {
        newAttachmentsEventListener: eventEmitter,
        pendingJobs$: new BehaviorSubject<AttachmentUploadJob[]>([]),
      });
    }
    return this.folderContext.get(ROOT_CONTEXT_KEY)!;
  }

  findFolderById(id: string): AttachmentFolderInstance | undefined {
    for (const folder of this.folders()) {
      if (folder.id === id) {
        return folder;
      }
      const subFolder = folder.subFolders?.find((sub) => sub.id === id);
      if (subFolder) {
        return subFolder;
      }
    }
    return undefined;
  }

  attachmentsAddedToFolder(folderId: string, event: Attachment[]) {
    const value = this.value;
    const folder = this.findFolderById(folderId);
    if (!value || !folder) {
      return;
    }
    folder.attachments = [...(folder.attachments ?? []), ...event];
    this.value = { ...value };
    this.reloadAttachmentsWithProfilePictures.set(this.reloadAttachmentsWithProfilePictures() + 1);
  }

  attachmentsAddedToRoot(event: Attachment[]) {
    const value = this.value;
    if (!value) {
      return;
    }
    this.value = { ...value, rootAttachments: [...(value.rootAttachments ?? []), ...event] };
    this.reloadAttachmentsWithProfilePictures.set(this.reloadAttachmentsWithProfilePictures() + 1);
  }

  openAttachment(attachment: Attachment): void {
    const a = document.createElement('a');
    a.href = attachment.presignedUrl;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async deleteAttachment(
    attachment: Attachment,
    attachmentFolderInstance: AttachmentFolderInstance | undefined,
  ): Promise<void> {
    this.nbDialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Ausgewählte Datei löschen?',
        message: 'Sind Sie sicher, dass Sie die ausgewählte Datei löschen willst?',
      },
    }).onClose.subscribe({
      next: (result) => {
        if (result) {
          const attachments = attachmentFolderInstance?.attachments ?? this.rootAttachments();
          const toDelete = attachments?.find((a) => a.s3key === attachment.s3key);
          if (toDelete) {
            toDelete.deleted = true;
            this.value = { ...this.value! };
            this.reloadAttachmentsWithProfilePictures.set(this.reloadAttachmentsWithProfilePictures() + 1);
          }
        }
      }, error: () => {
        this.nbToastrService.danger('Beim Löschen der Datei ist ein Fehler aufgetreten', 'Datei löschen');
      },
    });
  }

  documentCount(instance: AttachmentFolderInstance): number {
    return instance.attachments?.filter(a => !a.deleted).length ?? 0;
  }

  subFolderCount(instance: AttachmentFolderInstance): number {
    return instance.subFolders?.length ?? 0;
  }

  folderIsEmpty(instance: AttachmentFolderInstance): boolean {
    return this.documentCount(instance) === 0 && this.subFolderCount(instance) === 0;
  }

  async editAttachment(
    attachment: Attachment,
    attachmentFolderInstance: AttachmentFolderInstance | undefined,
  ): Promise<void> {
    try {
      const result = (await firstValueFrom(this.nbDialogService.open(AttachmentDialogComponent,
        {
          context: {
            attachment: attachment,
          },
        }).onClose));
      if (result) {
        const attachments = attachmentFolderInstance?.attachments ?? this.rootAttachments();
        const target = attachments?.find((a) => a.s3key === attachment.s3key) ?? attachment;
        if (result.tags) {
          target.tags = result.tags;
          target.comment = result.comment;
        }
        this.value = { ...this.value! };
        this.reloadAttachmentsWithProfilePictures.set(this.reloadAttachmentsWithProfilePictures() + 1);
      }
    } catch (e) {
      console.error(e);
      this.nbToastrService.danger('Fehler beim bearbeiten der Meta-Daten', 'Fehler');
    }
  }
}
